import { ModalConnectionAddition, ModalTableSetting } from "@/features/modals";
import { currentPersonSelector } from "@/pages/ContactsPage/slice";
import { useAppSelector } from "@/services/store";
import { ModalDeleteConnection } from "@/features/modals/ModalDeleteConnection/ui/ModalDeleteConnection";
import { ResponsiveBlockSwitcher } from "@/shared/Components";
import type { ConnectionFormValues } from "@/features/modals/ModalConnectionAddition/schema";
import type { Person } from "@/services/api";
import type { ModalTableSettingFormValues } from "@/features/modals/ModalTableSetting/schema";
import type { ConnectionsColumnsState } from "../../model/types";
import { useConnectionsTable } from "../../lib";
import { Desktop } from "../Desktop";
import { Mobile } from "../Mobile";

const EDIT_CONNECTION_INDEX = 0;

export const ConnectionsTable = () => {
  const { state, methods } = useConnectionsTable();

  const {
    editPersonConnection,
    closeEditModal,
    closeCreateModal,
    setColumnsState,
    createNewPersonConnection,
    deleteSelectedConnections,
    closeSettingsModal,
    closeDeleteModal,
  } = methods;
  const {
    deleteModalIsOpen,
    editModalIsOpen,
    selectedIds,
    tableColumnsState,
    tableData,
    createModalIsOpen,
    settingsModalIsOpen,
  } = state;

  const currentPerson = useAppSelector(currentPersonSelector);

  const createOnSubmit = (data: ConnectionFormValues) => {
    if (!data.personId || !currentPerson) return;
    createNewPersonConnection({
      additionalInfo: data.additionalInfo ?? "",
      connectionPersonId: data.personId,
      personConnectionType: { name: data.connection ?? "" },
      personId: currentPerson?.id,
    });
    closeCreateModal();
  };

  const editOnSubmit = (data: ConnectionFormValues) => {
    if (!data.personId || !data.id || !currentPerson || selectedIds.length > 1) return;
    editPersonConnection({
      id: data.id,
      additionalInfo: data.additionalInfo ?? "",
      connectionPersonId: data.personId,
      personConnectionType: { name: data.connection ?? "" },
      personId: currentPerson?.id,
    });
    closeEditModal();
  };

  const deleteOnSubmit = (data: Person["personConnections"]) => {
    deleteSelectedConnections(data.map((item) => item.id));
    closeDeleteModal();
  };

  const settingsOnSubmit = (data: ModalTableSettingFormValues) => {
    setColumnsState(data.columnsState as ConnectionsColumnsState);
    closeSettingsModal();
  };

  const currentEditConnection = tableData.find(
    (item) => item.id === selectedIds[EDIT_CONNECTION_INDEX],
  );

  return (
    <>
      <ResponsiveBlockSwitcher desktop={<Desktop />} mobile={<Mobile />} />
      <ModalConnectionAddition
        onSubmit={createOnSubmit}
        onCancel={closeCreateModal}
        open={createModalIsOpen}
      />
      <ModalConnectionAddition
        defaultValues={
          currentEditConnection && {
            additionalInfo: currentEditConnection.additionalInfo,
            connection: currentEditConnection.personConnectionType?.name ?? null,
            personId: currentEditConnection.connectionPersonId,
            id: currentEditConnection.id,
          }
        }
        onSubmit={editOnSubmit}
        onCancel={closeEditModal}
        open={editModalIsOpen}
      />
      <ModalTableSetting
        defaultValues={{ columnsState: tableColumnsState }}
        onCancel={closeSettingsModal}
        onSubmit={settingsOnSubmit}
        open={settingsModalIsOpen}
      />
      <ModalDeleteConnection
        defaultValues={
          currentPerson?.personConnections.filter((item) => {
            return !!selectedIds.find((id) => id === item.id);
          }) ?? []
        }
        isModalOpen={deleteModalIsOpen}
        onCancel={closeDeleteModal}
        onSubmit={deleteOnSubmit}
      />
    </>
  );
};
